import moment from 'moment';
import { faPencil, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Spinner } from '../../../../../components/Spinner';
import { useState } from 'react';
import { getDaysInMonth, nextYears, previousYears } from '../../../../../utils/dates';
import { CircularProgress } from '@mui/material';

function ConsumerPopoverTerminationDate({ consumerLastEmploymentTerminationDate, updateConsumer }) {
  const [isOpen, setIsOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [terminationDate, setTerminationDate] = useState({
    year: String(moment(consumerLastEmploymentTerminationDate || undefined).year()),
    month: String(moment(consumerLastEmploymentTerminationDate || undefined).month()),
    date: String(moment(consumerLastEmploymentTerminationDate || undefined).date()),
  });

  const handleSave = async () => {
    setSpinner(true);
    await updateConsumer({
      lastEmploymentTerminationDate: moment({
        year: terminationDate.year,
        month: terminationDate.month,
        date: terminationDate.date,
      }).format('YYYY-MM-DD'),
    });
    setSpinner(false);
    setIsOpen(false);
  };

  const handleDelete = async () => {
    setIsOpen(false);
    setDeleting(true);
    await updateConsumer({
      lastEmploymentTerminationDate: null,
    });
    setDeleting(false);
  };

  const handleToggle = () => setIsOpen(!isOpen);

  return (
    <>
      <Form.Label className="bo-consumer-input-label mt-3">
        Last Employment Termination Date
      </Form.Label>
      <div className="bo-consumer-text-input-container">
        <OverlayTrigger
          trigger="click"
          placement="bottom-start"
          show={isOpen}
          popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }}
          overlay={
            <Popover className="custom-popover">
              <Popover.Body className="bo-consumer-popover-form-menu">
                <Spinner visible={spinner} size={36}>
                  <Form.Label className="bo-consumer-popover-form-menu-label">Year</Form.Label>
                  <Form.Select
                    className="bo-consumer-popover-form-menu-input bo-w-150"
                    value={terminationDate.year}
                    onChange={e => {
                      if (
                        !moment({
                          year: e.target.value,
                          month: terminationDate.month,
                          date: terminationDate.date,
                        }).isValid()
                      ) {
                        setTerminationDate({
                          ...terminationDate,
                          year: e.target.value,
                          date: moment({ year: e.target.value, month: terminationDate.month })
                            .endOf('month')
                            .date(),
                        });
                      } else {
                        setTerminationDate({ ...terminationDate, year: e.target.value });
                      }
                    }}
                  >
                    {[...previousYears(10), ...nextYears(20, moment().add(1, 'year').year())].map(
                      year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ),
                    )}
                  </Form.Select>
                  <Form.Label className="bo-consumer-popover-form-menu-label mt-2">
                    Month
                  </Form.Label>
                  <Form.Select
                    className="bo-consumer-popover-form-menu-input bo-w-150"
                    value={terminationDate.month}
                    onChange={e => {
                      if (
                        !moment({
                          year: terminationDate.year,
                          month: e.target.value,
                          date: terminationDate.date,
                        }).isValid()
                      ) {
                        setTerminationDate({
                          ...terminationDate,
                          month: e.target.value,
                          date: moment({ year: terminationDate.year, month: e.target.value })
                            .endOf('month')
                            .date(),
                        });
                      } else {
                        setTerminationDate({ ...terminationDate, month: e.target.value });
                      }
                    }}
                  >
                    {[...Array(12)].map((_, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <option value={i} key={i}>
                        {moment().month(i).format('MMMM')}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Label className="bo-consumer-popover-form-menu-label mt-2">Day</Form.Label>
                  <Form.Select
                    className="bo-consumer-popover-form-menu-input bo-w-150"
                    value={terminationDate.date}
                    onChange={e => setTerminationDate({ ...terminationDate, date: e.target.value })}
                  >
                    {getDaysInMonth(terminationDate.month, terminationDate.year).map(x => (
                      <option value={x} key={x}>
                        {x}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="mt-2 d-flex justify-content-between">
                    <Button className="bo-common-button" onClick={handleSave}>
                      Save
                    </Button>
                    <Button className="bo-common-button" onClick={handleToggle}>
                      Cancel
                    </Button>
                  </div>
                </Spinner>
              </Popover.Body>
            </Popover>
          }
        >
          <Button
            className="border-0 bg-transparent d-flex m-0 p-0 w-100 d-flex align-items-center"
            onClick={handleToggle}
          >
            <div className="me-auto">
              <Form.Control
                className="bo-consumer-text-input bo-w-150"
                type="text"
                readOnly
                value={
                  consumerLastEmploymentTerminationDate
                    ? moment(consumerLastEmploymentTerminationDate).format('YYYY-MM-DD')
                    : ''
                }
              />
            </div>
            <div
              className={`bo-consumer-input-button-container${
                deleting ? ' bo-consumer-input-button-container-active' : ''
              }`}
            >
              {deleting ? (
                <div className="h-100 text-dark">
                  <CircularProgress color="inherit" size={16} />
                </div>
              ) : (
                <div className="bo-consumer-input-button-icon">
                  <FontAwesomeIcon icon={faPencil} color="#5946A4" size="sm" />
                </div>
              )}
            </div>
          </Button>
        </OverlayTrigger>
        {!deleting && (
          <div className="bo-consumer-trash-button-container">
            <Button
              className="bo-consumer-input-button-icon"
              onClick={handleDelete}
              key="delete"
              disabled={spinner}
            >
              <FontAwesomeIcon icon={faTrashAlt} color="#5946A4" size="sm" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default ConsumerPopoverTerminationDate;
